import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Row, Col, Card, Alert, Button } from 'react-bootstrap';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import DatePeriodNavigation, { PeriodChangeEvent } from '../components/common/DatePeriodNavigation';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { RootState } from '../store';
import { getEvents, postEventsSync, useGetUsersId } from '../api/generated/arrayscapeGamingDashboardAPI';
import type { Event, EventsSummary, JsonPatchOperation } from '../api/generated/model';
import EventEditModal from '../components/EventEditModal';
import HostDetailModal from '../components/HostDetailModal';
import InvoiceDetailModal from '../components/InvoiceDetailModal';
import { useUpdateEvent } from '../hooks/useUpdateEvent';
import EventFilterBar, { EventFiltersState } from '../components/EventFilterBar';
import { SyncButton } from '../components/SyncButton';
import { useSyncState } from '../hooks/useSyncState';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';

// Import DataTable system
import { DataTable } from '../components/common/DataTable';
import type { ColumnConfig } from '../components/common/DataTable/types';
import {
  renderCode,
  renderSemibold,
  renderEmailLink,
  renderPhoneLink,
  renderPrice,
  renderDateTime,
  renderStatusBadge,
  renderInvoicedBadge,
  renderActivityTypeBadge,
  renderDeviceBadges,
  renderFormatBadge,
  renderCustomerTypeBadge,
  renderWrappedText,
  renderHostWithTooltip,
} from '../components/common/DataTable';


type EventColumnKey =
  | 'id' | 'customer' | 'location' | 'state' | 'date_time' | 'activity_type'
  | 'activity' | 'age_range' | 'devices' | 'format' | 'contact_name'
  | 'contact_email' | 'contact_phone' | 'reviewed' | 'tech_setup_required'
  | 'tech_setup_completed' | 'pre_program_setup_email_sent' | 'pre_program_reminder_email_sent'
  | 'sling_shift_time' | 'host' | 'customer_type' | 'price' | 'invoiced' | 'invoice'
  | 'sales_person' | 'sales_lead' | 'owner' | 'program_notes' | 'internal_notes';

// sling_shift_time is deliberately absent: it is derived from the assigned
// shifts, so there is nothing for a hand-typed value to be stored in.
type EditableField =
  | 'reviewed'
  | 'tech_setup_required'
  | 'tech_setup_completed'
  | 'pre_program_setup_email_sent'
  | 'pre_program_reminder_email_sent'
  | 'invoiced'
  | 'owner_id'
  | 'internal_notes';

const eventFilterParamKeys = [
  'sales_person', 'sales_lead', 'owner', 'customer', 'location', 'state',
  'activity', 'activity_type', 'device_types', 'reviewed',
  'tech_setup_required', 'tech_setup_completed', 'invoiced',
  'invoice_number', 'customer_type', 'need_review', 'need_tech_setup',
  'need_pre_program_email', 'need_reminder_email', 'need_sling_shift',
  'sling_location', 'sling_error',
] as const;

const validPeriods = ['month', 'quarter', 'year', 'custom', 'all'] as const;
type Period = typeof validPeriods[number];

function parseDate(s: string | null): string | null {
  if (!s) return null;
  const parts = s.split('-');
  if (parts.length !== 3) return null;
  const y = Number(parts[0]), m = Number(parts[1]), day = Number(parts[2]);
  if (isNaN(y) || isNaN(m) || isNaN(day)) return null;
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function monthRange(d: Date): { start_date: string; end_date: string } {
  const y = d.getFullYear(), m = d.getMonth();
  const start = new Date(y, m, 1);
  const end = new Date(y, m + 1, 0);
  return { start_date: start.toLocaleDateString('en-CA'), end_date: end.toLocaleDateString('en-CA') };
}

function quarterRange(d: Date): { start_date: string; end_date: string } {
  const y = d.getFullYear(), q = Math.floor(d.getMonth() / 3);
  const start = new Date(y, q * 3, 1);
  const end = new Date(y, q * 3 + 3, 0);
  return { start_date: start.toLocaleDateString('en-CA'), end_date: end.toLocaleDateString('en-CA') };
}

function yearRange(d: Date): { start_date: string; end_date: string } {
  const y = d.getFullYear();
  return { start_date: `${y}-01-01`, end_date: `${y}-12-31` };
}

function periodRange(period: Period): { start_date: string; end_date: string } {
  const now = new Date();
  if (period === 'quarter') return quarterRange(now);
  if (period === 'year') return yearRange(now);
  return monthRange(now);
}

function parseInitialDateParams(params: URLSearchParams): { period: Period; start_date: string; end_date: string } {
  const rawPeriod = params.get('period');
  const period: Period = rawPeriod && (validPeriods as readonly string[]).includes(rawPeriod)
    ? rawPeriod as Period
    : 'month';

  // 'all' mode: emit empty dates so the API call drops the start/end params.
  if (period === 'all') {
    return { period, start_date: '', end_date: '' };
  }

  let startDate = parseDate(params.get('start_date'));
  let endDate = parseDate(params.get('end_date'));

  if (period !== 'custom') {
    const defaults = periodRange(period);
    if (!startDate) startDate = defaults.start_date;
    if (!endDate) endDate = defaults.end_date;
  } else {
    const defaults = monthRange(new Date());
    if (!startDate) startDate = defaults.start_date;
    if (!endDate) endDate = defaults.end_date;
  }

  if (startDate > endDate) {
    [startDate, endDate] = [endDate, startDate];
  }

  return { period, start_date: startDate, end_date: endDate };
}

const EventsNew: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [initialDateParams] = useState(() => parseInitialDateParams(searchParams));
  const { user } = useSelector((state: RootState) => state.auth);

  // Editable event fields (owner, reviewed, invoiced, …) are all written via
  // PATCH /events/{id}, which is restricted to admin + events_manager. Gate the
  // click-to-edit affordance on the same roles so unauthorized users just see
  // the values rather than opening a modal that would 403 on save.
  const canEditEvents = !!user?.role && ['admin', 'events_manager'].includes(user.role);

  const [sortField, setSortField] = useState<EventColumnKey>(
    (searchParams.get('sort_field') as EventColumnKey) || 'date_time'
  );
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(
    (searchParams.get('sort_dir') as 'asc' | 'desc') || 'desc'
  );

  // Ref for scroll container
  const cardBodyRef = useRef<HTMLDivElement>(null);

  // Get sync events state from notification context
  const eventsSyncState = useSyncState('events');

  // Filter bar toggle state
  const [showFilterBar, setShowFilterBar] = useState(false);

  // Event editing modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);

  // Invoice detail modal state
  const [invoiceModalId, setInvoiceModalId] = useState<string | null>(null);

  // Host detail modal state
  const [showHostModal, setShowHostModal] = useState(false);
  const [hostEventId, setHostEventId] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<EditableField | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  // Track modified events and fields for highlighting
  const [modifiedEvents, setModifiedEvents] = useState<Map<string, Set<string>>>(new Map());

  // Track when events have just been refreshed after sync
  const [justRefreshed, setJustRefreshed] = useState(false);

  // Mutation hook for updating events
  const updateEventMutation = useUpdateEvent();

  // Fetch user preferences
  const { data: userData } = useGetUsersId(user?.id || '', {
    query: {
      enabled: !!user?.id,
    }
  });

  const [period, setPeriod] = useState<Period>(initialDateParams.period);

  // Filters state — initialize from URL params
  const [filters, setFilters] = useState(() => {
    const initial: Record<string, any> = {
      dateRange: {
        start_date: initialDateParams.start_date,
        end_date: initialDateParams.end_date,
      },
    };
    for (const key of eventFilterParamKeys) {
      const val = searchParams.get(key);
      if (val) initial[key] = val;
    }
    return initial as EventFiltersState & { dateRange: { start_date: string; end_date: string } };
  });

  // Fixed large limit for infinite scroll
  const FETCH_LIMIT = 50;

  // Row identity. Shared with the hook's duplicate-detection and DataTable.
  const getRowKey = useCallback((event: Event) => event.id || '', []);

  // Build query params - for cursor-based pagination, only send cursor + limit
  const buildQueryParams = useCallback(
    (cursor: string | undefined) =>
      cursor
        ? { cursor, limit: FETCH_LIMIT }
        : {
            start_date: filters.dateRange.start_date || undefined,
            end_date: filters.dateRange.end_date || undefined,
            sort_field: sortField as any,
            sort_direction: sortDirection,
            sales_person: filters.sales_person,
            sales_lead: filters.sales_lead,
            owner: filters.owner,
            customer: filters.customer,
            location: filters.location,
            state: filters.state,
            activity: filters.activity,
            activity_type: filters.activity_type,
            device_types: filters.device_types,
            reviewed: filters.reviewed,
            tech_setup_required: filters.tech_setup_required,
            tech_setup_completed: filters.tech_setup_completed,
            invoiced: filters.invoiced,
            invoice_number: filters.invoice_number,
            customer_type: filters.customer_type,
            need_review: filters.need_review,
            need_tech_setup: filters.need_tech_setup,
            need_pre_program_email: filters.need_pre_program_email,
            need_reminder_email: filters.need_reminder_email,
            need_sling_shift: filters.need_sling_shift,
            sling_location: filters.sling_location,
            sling_error: filters.sling_error,
            limit: FETCH_LIMIT,
          },
    [filters, sortField, sortDirection]
  );

  const {
    allItems: events,
    totalCount,
    apiSummary,
    isLoading,
    error,
    hasMore,
    handleLoadMore,
    setAllItems: setAllEvents,
  } = usePaginatedQuery<Event, Awaited<ReturnType<typeof getEvents>>, EventsSummary>({
    queryKey: 'events-new',
    buildQueryParams,
    queryFn: (params) => getEvents(params as any),
    getRowKey,
    enabled: period === 'all' || (!!filters.dateRange.start_date && !!filters.dateRange.end_date),
  });

  // Clear highlighting when filters or sort change
  useEffect(() => {
    setModifiedEvents(new Map());
  }, [filters, sortField, sortDirection]);

  // Use summary from API if available, otherwise fall back to client-side calculation
  const summary = apiSummary ? {
    total_events: totalCount ?? events.length,
    need_review: apiSummary.need_review ?? 0,
    need_tech_setup: apiSummary.need_tech_setup ?? 0,
    need_pre_program_email: apiSummary.need_pre_program_email ?? 0,
    need_reminder_email: apiSummary.need_reminder_email ?? 0,
    need_sling_shift: apiSummary.need_sling_shift ?? 0,
    sling_error: apiSummary.sling_error ?? 0,
  } : {
    total_events: totalCount ?? events.length,
    // Client-side fallback calculation for "need" counts (only sees loaded events)
    need_review: events.filter((e: Event) => e.reviewed !== 'Yes').length,
    need_tech_setup: events.filter((e: Event) => e.tech_setup_required === 'Yes' && e.tech_setup_completed !== 'Yes').length,
    need_pre_program_email: events.filter((e: Event) => e.pre_program_setup_email_sent !== 'Yes').length,
    need_reminder_email: events.filter((e: Event) => e.pre_program_reminder_email_sent !== 'Yes').length,
    need_sling_shift: events.filter((e: Event) => !e.shifts || e.shifts.length === 0).length,
    // Mirrors the server's sling_error summary: a mapping error on the event, or
    // any assigned shift that cannot be covering it.
    sling_error: events.filter(
      (e: Event) => !!e.sling_error || !!e.shifts?.some(s => s.event_time_mismatch)
    ).length,
  };

  // Handle period changes from the navigation component
  const handlePeriodChange = (event: PeriodChangeEvent) => {
    setPeriod(event.navigationMode as Period);
    setFilters(prev => ({ ...prev, dateRange: event.dateRange }));
  };

  // Handle clicking on summary cards to toggle filters
  const handleNeedFilterToggle = (filterKey: 'need_review' | 'need_tech_setup' | 'need_pre_program_email' | 'need_reminder_email' | 'need_sling_shift' | 'sling_error') => {
    setFilters(prev => ({
      ...prev,
      // Toggle the clicked filter on/off, keeping other filters active
      [filterKey]: prev[filterKey] ? undefined : 'true'
    }));
  };

  // Handle filter changes from EventFilters component
  const handleFiltersChange = (newFilters: EventFiltersState) => {
    setFilters({
      dateRange: newFilters.dateRange,
      sales_person: newFilters.sales_person,
      sales_lead: newFilters.sales_lead,
      owner: newFilters.owner,
      customer: newFilters.customer,
      location: newFilters.location,
      state: newFilters.state,
      activity: newFilters.activity,
      activity_type: newFilters.activity_type,
      device_types: newFilters.device_types,
      reviewed: newFilters.reviewed,
      tech_setup_required: newFilters.tech_setup_required,
      tech_setup_completed: newFilters.tech_setup_completed,
      invoiced: newFilters.invoiced,
      invoice_number: newFilters.invoice_number,
      customer_type: newFilters.customer_type,
      need_review: newFilters.need_review,
      need_tech_setup: newFilters.need_tech_setup,
      need_pre_program_email: newFilters.need_pre_program_email,
      need_reminder_email: newFilters.need_reminder_email,
      need_sling_shift: newFilters.need_sling_shift,
      sling_location: newFilters.sling_location,
      sling_error: newFilters.sling_error,
    });
  };

  // Handle clear filters from EventFilters component
  const handleClearFilters = () => {
    setFilters(prev => ({
      dateRange: prev.dateRange, // Keep date range
      sales_person: undefined,
      sales_lead: undefined,
      owner: undefined,
      customer: undefined,
      location: undefined,
      state: undefined,
      activity: undefined,
      activity_type: undefined,
      device_types: undefined,
      reviewed: undefined,
      tech_setup_required: undefined,
      tech_setup_completed: undefined,
      invoiced: undefined,
      invoice_number: undefined,
      customer_type: undefined,
      need_review: undefined,
      need_tech_setup: undefined,
      need_pre_program_email: undefined,
      need_reminder_email: undefined,
      need_sling_shift: undefined,
      sling_location: undefined,
      sling_error: undefined,
    }));
  };

  // Sync filter/sort state to URL params
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('period', period);
    if (filters.dateRange.start_date) params.set('start_date', filters.dateRange.start_date.slice(0, 10));
    if (filters.dateRange.end_date) params.set('end_date', filters.dateRange.end_date.slice(0, 10));
    for (const key of eventFilterParamKeys) {
      const val = (filters as any)[key];
      if (val) params.set(key, val);
    }
    if (sortField !== 'date_time') params.set('sort_field', sortField);
    if (sortDirection !== 'desc') params.set('sort_dir', sortDirection);
    setSearchParams(params, { replace: true });
  }, [filters, period, sortField, sortDirection, setSearchParams]);

  // Count active filters (excluding dateRange and "need" filters)
  const activeFilterCount = Object.entries(filters).filter(([key, value]) => {
    return value &&
           key !== 'dateRange' &&
           key !== 'need_review' &&
           key !== 'need_tech_setup' &&
           key !== 'need_pre_program_email' &&
           key !== 'need_reminder_email' &&
           key !== 'need_sling_shift' &&
           key !== 'sling_error';
  }).length;

  // Handle event sync completion
  useEffect(() => {
    if (eventsSyncState && eventsSyncState.status === 'completed') {
      // Refresh events after sync completes
      // Invalidate and refetch the queries
      queryClient.invalidateQueries({ queryKey: ['events-new'], refetchType: 'active' });

      // Set refresh indicator
      setJustRefreshed(true);
      setTimeout(() => setJustRefreshed(false), 2000);
    }
  }, [eventsSyncState, queryClient]);

  // Handle event sync
  const handleSyncEvents = async (): Promise<string | undefined> => {
    try {
      const resp = await postEventsSync();
      // The backend will emit sync events via SSE
      return (resp as any)?.sync_id;
    } catch (error) {
      console.error('Error triggering events sync:', error);
      return undefined;
    }
  };

  // Handle clicking on an editable cell
  const handleCellClick = (event: Event, field: EditableField) => {
    if (!canEditEvents) return;
    setEditingEvent(event);
    setEditingField(field);
    setEditError(null);
    setShowEditModal(true);
  };

  // Handle saving event updates
  const handleSaveEvent = async (eventId: string, patch: JsonPatchOperation[]) => {
    try {
      // Execute mutation and get the updated event from response
      const updatedEvent = await updateEventMutation.mutateAsync({ id: eventId, patch });

      // Track which fields were modified for highlighting
      if (editingField) {
        setModifiedEvents(prev => {
          const newMap = new Map(prev);
          const eventFields = newMap.get(eventId) || new Set<string>();
          eventFields.add(editingField);
          newMap.set(eventId, eventFields);
          return newMap;
        });
      }

      // Invalidate events query to refetch with updated data
      queryClient.invalidateQueries({ queryKey: ['events-new'] });

      setShowEditModal(false);
      setEditingEvent(null);
      setEditingField(null);
      setEditError(null);
    } catch (error: any) {
      console.error('Failed to update event:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Failed to update event';
      setEditError(errorMessage);
      throw error;
    }
  };

  // Handle closing the modal
  const handleCloseModal = () => {
    setShowEditModal(false);
    setEditingEvent(null);
    setEditingField(null);
    setEditError(null);
  };

  // Removed handleRowClick - editable cells now handle clicks individually

  // Check if a specific cell was modified
  const isCellModified = (eventId: string | undefined, fieldName: string): boolean => {
    if (!eventId) return false;
    const eventFields = modifiedEvents.get(eventId);
    return eventFields ? eventFields.has(fieldName) : false;
  };

  // Check if an event has any modifications
  const isEventModified = (eventId: string | undefined): boolean => {
    if (!eventId) return false;
    return modifiedEvents.has(eventId);
  };

  // Handle sort change
  const handleSortChange = useCallback((field: string, direction: 'asc' | 'desc') => {
    setSortField(field as EventColumnKey);
    setSortDirection(direction);
  }, []);

  // Define column configuration
  const columns: ColumnConfig<Event>[] = [
    {
      key: 'id',
      label: 'Event ID',
      sortable: true,
      width: 120,
      renderCell: (event) => renderCode(event.id),
      tooltip: (event) => event.id || '',
    },
    {
      key: 'customer',
      label: 'Customer',
      sortable: true,
      width: 200,
      renderCell: (event) => {
        const customerName = event.customer?.name;
        const customerId = event.customer?.id;
        if (!customerName || !customerId) return renderSemibold(customerName);
        return (
          <a href={`/customers/${customerId}`} className="text-decoration-none fw-semibold">
            {customerName}
          </a>
        );
      },
      tooltip: (event) => event.customer?.name || '',
    },
    {
      key: 'location',
      label: 'Location',
      sortable: true,
      width: 180,
      renderCell: (event) => event.program?.location || null,
      tooltip: (event) => event.program?.location || '',
    },
    {
      key: 'state',
      label: 'State',
      sortable: true,
      width: 113,
      renderCell: (event) => event.customer?.primary_source?.address?.state || null,
      tooltip: (event) => event.customer?.primary_source?.address?.state || '',
    },
    {
      key: 'date_time',
      label: 'Date/Time',
      sortable: true,
      width: 240,
      renderCell: (event) => {
        if (!event.program?.time) return null;
        const time = event.program.time;
        return renderDateTime({
          startTime: time.start?.time,
          endTime: time.end?.time,
          timezone: time.start?.timezone,
          tbd: time.tbd,
          hasDay: time.start?.has_day,
          hasTime: time.start?.has_time,
          endHasTime: time.end?.has_time,
        });
      },
      tooltip: (event) => {
        const time = event.program?.time;
        if (!time?.start?.time) return '';
        return `${time.start.time} - ${time.end?.time || 'TBD'}`;
      },
    },
    {
      key: 'activity_type',
      label: 'Activity Type',
      sortable: true,
      width: 140,
      renderCell: (event) => renderActivityTypeBadge(event.program?.type),
      tooltip: (event) => event.program?.type || '',
    },
    {
      key: 'activity',
      label: 'Activity',
      sortable: true,
      width: 180,
      renderCell: (event) => renderSemibold(event.program?.program),
      tooltip: (event) => event.program?.program || '',
    },
    {
      key: 'age_range',
      label: 'Age Range',
      sortable: true,
      width: 121,
      renderCell: (event) => event.program?.age || null,
      tooltip: (event) => event.program?.age || '',
    },
    {
      key: 'devices',
      label: 'Devices',
      sortable: true,
      width: 133,
      renderCell: (event) => renderDeviceBadges(event.program?.devices),
      tooltip: (event) => event.program?.devices || '',
    },
    {
      key: 'format',
      label: 'Format',
      sortable: true,
      width: 128,
      renderCell: (event) => renderFormatBadge(event.program?.format),
      tooltip: (event) => event.program?.format || '',
    },
    {
      key: 'contact_name',
      label: 'Contact Name',
      sortable: true,
      width: 180,
      renderCell: (event) => event.program?.contact?.name || null,
      tooltip: (event) => event.program?.contact?.name || '',
    },
    {
      key: 'contact_email',
      label: 'Contact Email',
      sortable: true,
      width: 220,
      renderCell: (event) => renderEmailLink(event.program?.contact?.email),
      tooltip: (event) => event.program?.contact?.email || '',
    },
    {
      key: 'contact_phone',
      label: 'Contact Phone',
      sortable: true,
      width: 140,
      renderCell: (event) => renderPhoneLink(event.program?.contact?.phone),
      tooltip: (event) => event.program?.contact?.phone || '',
    },
    {
      key: 'reviewed',
      label: 'Reviewed?',
      sortable: true,
      editable: canEditEvents,
      width: 155,
      onCellClick: (event) => handleCellClick(event, 'reviewed'),
      renderCell: (event) => renderStatusBadge(event.reviewed),
      tooltip: (event) => `Click to edit: ${event.reviewed || ''}`,
    },
    {
      key: 'tech_setup_required',
      label: 'Tech Setup Req?',
      sortable: true,
      editable: canEditEvents,
      width: 140,
      onCellClick: (event) => handleCellClick(event, 'tech_setup_required'),
      renderCell: (event) => renderStatusBadge(event.tech_setup_required),
      tooltip: (event) => `Click to edit: ${event.tech_setup_required || ''}`,
    },
    {
      key: 'tech_setup_completed',
      label: 'Tech Setup Completed',
      sortable: true,
      editable: canEditEvents,
      width: 160,
      onCellClick: (event) => handleCellClick(event, 'tech_setup_completed'),
      renderCell: (event) => renderStatusBadge(event.tech_setup_completed),
      tooltip: (event) => `Click to edit: ${event.tech_setup_completed || ''}`,
    },
    {
      key: 'pre_program_setup_email_sent',
      label: 'Pre-Program Setup Email Sent?',
      sortable: true,
      editable: canEditEvents,
      width: 200,
      onCellClick: (event) => handleCellClick(event, 'pre_program_setup_email_sent'),
      renderCell: (event) => renderStatusBadge(event.pre_program_setup_email_sent),
      tooltip: (event) => `Click to edit: ${event.pre_program_setup_email_sent || ''}`,
    },
    {
      key: 'pre_program_reminder_email_sent',
      label: 'Reminder Email Sent?',
      sortable: true,
      editable: canEditEvents,
      width: 180,
      onCellClick: (event) => handleCellClick(event, 'pre_program_reminder_email_sent'),
      renderCell: (event) => renderStatusBadge(event.pre_program_reminder_email_sent),
      tooltip: (event) => `Click to edit: ${event.pre_program_reminder_email_sent || ''}`,
    },
    {
      key: 'sling_shift_time',
      label: 'Sling Shift Time',
      sortable: false,
      width: 150,
      renderCell: (event) => {
        if (!event.sling_shift_time) return null;
        // One cell covers every assigned shift, so the per-shift error is rolled
        // up here: flag the cell if any shift can't be covering this event, and
        // let Shift Details name the offending one. The icon leads so it stays
        // visible when the text is truncated to the column width.
        const mismatch = event.shifts?.some(s => s.event_time_mismatch);
        if (!mismatch) return event.sling_shift_time;
        return (
          <span
            className="text-danger fw-semibold"
            title={`${event.sling_shift_time} — a shift assigned to this event does not overlap its scheduled time, so it cannot be covering it. Open Host for details.`}
          >
            <i className="bi bi-exclamation-triangle-fill me-1"></i>
            {event.sling_shift_time}
          </span>
        );
      },
    },
    {
      key: 'host',
      label: 'Host',
      sortable: true,
      width: 150,
      onCellClick: (event) => {
        setHostEventId(event.id || null);
        setShowHostModal(true);
      },
      renderCell: (event) => {
        // Check if event has a sling location mapping
        const programLocation = event.program?.location || '';
        const locationMapping = event.customer?.locations?.find(
          (m: any) => (m.location || '') === programLocation
        );
        const hasSlingMapping = !!locationMapping?.sling_location_id;
        const hasShifts = !!event.shifts && event.shifts.length > 0;

        // Render assigned host(s), if any — shown even when the mapping is broken
        // so staff can see who's on it and which Sling location the shift is at.
        const hostEls = hasShifts ? (
          <div className="d-flex flex-column gap-1">
            {event.shifts!.map((shift, i) => {
              const userName = shift.user
                ? `${shift.user.first_name || ''} ${shift.user.last_name || ''}`.trim()
                : 'Unknown';
              const shiftConfig = {
                userName,
                summary: shift.summary,
                status: shift.status,
                type: shift.type,
                locationName: shift.location?.name,
                startTime: shift.start_time,
                endTime: shift.end_time,
                timezone: event.program?.time?.start?.timezone,
                mismatch: shift.event_time_mismatch,
              };
              return (
                <React.Fragment key={shift.id || i}>
                  {renderHostWithTooltip(userName, undefined, shiftConfig, `${event.id}-${i}`)}
                </React.Fragment>
              );
            })}
          </div>
        ) : null;

        // Actionable Sling mapping error takes precedence — surface it with the
        // full message on hover, and still show the assigned host underneath.
        if (event.sling_error) {
          return (
            <div className="d-flex flex-column gap-1">
              <span className="badge" title={event.sling_error} style={{ backgroundColor: '#dc3545', color: '#fff', fontSize: '0.875rem', whiteSpace: 'normal', textAlign: 'left' }}>
                <i className="bi bi-exclamation-triangle-fill me-1"></i>Sling Error
              </span>
              {hostEls}
            </div>
          );
        }

        if (!hasSlingMapping) {
          return (
            <span className="badge" style={{ backgroundColor: '#6c757d', color: '#fff', fontSize: '0.875rem', whiteSpace: 'normal', textAlign: 'left' }}>
              No Sling Location
            </span>
          );
        }

        if (!hasShifts) {
          return (
            <span className="badge" style={{ backgroundColor: '#dc3545', color: '#fff', fontSize: '0.875rem', whiteSpace: 'normal', textAlign: 'left' }}>
              Shift Not Assigned
            </span>
          );
        }

        return hostEls;
      },
    },
    {
      key: 'customer_type',
      label: 'Customer Type',
      sortable: true,
      width: 148,
      renderCell: (event) => renderCustomerTypeBadge(event.customer_type),
      tooltip: (event) => event.customer_type || 'Unknown',
    },
    {
      key: 'price',
      label: 'Price',
      sortable: true,
      width: 112,
      renderCell: (event) => renderPrice(event.program?.price),
      tooltip: (event) => event.program?.price !== undefined
        ? `$${event.program.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : '',
    },
    {
      key: 'invoiced',
      label: 'Invoiced?',
      sortable: true,
      editable: canEditEvents,
      width: 146,
      onCellClick: (event) => handleCellClick(event, 'invoiced'),
      renderCell: (event) => renderInvoicedBadge(event.invoiced),
      tooltip: (event) => `Click to edit: ${event.invoiced || ''}`,
    },
    {
      key: 'invoice',
      label: 'Invoice',
      sortable: true,
      width: 127,
      renderCell: (event) => {
        if (!event.invoice_id || !event.src_id) return null;
        const display = `${event.invoice_number || ''}${event.invoice_suffix || ''}`;
        return (
          <a
            href="#"
            className="text-decoration-none"
            onClick={(e) => {
              e.preventDefault();
              setInvoiceModalId(`${event.src_id}-${event.invoice_id}`);
            }}
          >
            {display}
          </a>
        );
      },
      tooltip: (event) => `${event.invoice_number || ''}${event.invoice_suffix || ''}`,
    },
    {
      key: 'sales_person',
      label: 'Salesperson',
      sortable: true,
      width: 168,
      renderCell: (event) => event.sales_person || null,
      tooltip: (event) => event.sales_person || '',
    },
    {
      key: 'sales_lead',
      label: 'Sales Lead',
      sortable: true,
      width: 150,
      renderCell: (event) => event.sales_lead || null,
      tooltip: (event) => event.sales_lead || '',
    },
    {
      key: 'owner',
      label: 'Owner',
      sortable: true,
      editable: canEditEvents,
      width: 168,
      onCellClick: (event) => handleCellClick(event, 'owner_id'),
      // Read from row data (renderCell closures capture columns once).
      renderCell: (event) => {
        const owner = event.owner;
        if (!owner) {
          return <span className="text-muted fst-italic">Unassigned</span>;
        }
        const name = `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || owner.email || owner.id;
        return (
          <span className="d-flex align-items-center gap-1 flex-wrap">
            <span>{name}</span>
            {owner.active === false && (
              <span className="badge bg-secondary">Deactivated</span>
            )}
          </span>
        );
      },
      tooltip: (event) => {
        const owner = event.owner;
        const name = owner
          ? (`${owner.first_name || ''} ${owner.last_name || ''}`.trim() || owner.email || owner.id || '')
          : 'Unassigned';
        return canEditEvents ? `Click to edit: ${name}` : name;
      },
    },
    {
      key: 'program_notes',
      label: 'Program Notes',
      sortable: true,
      width: 250,
      renderCell: (event) => renderWrappedText(event.program?.notes),
      tooltip: (event) => event.program?.notes || '',
    },
    {
      key: 'internal_notes',
      label: 'Internal Notes',
      sortable: true,
      editable: canEditEvents,
      width: 250,
      onCellClick: (event) => handleCellClick(event, 'internal_notes'),
      renderCell: (event) => renderWrappedText(event.internal_notes, 'small text-muted text-wrap'),
      tooltip: (event) => `Click to edit: ${event.internal_notes || ''}`,
    },
  ];

  const stickyHeaderContent = (
    <>
      <div className="summary-cards-row">
        <div className="summary-card-col">
          <Card className="h-100">
            <Card.Body className="text-center p-2">
              <div className="mb-2">
                <i className="bi bi-calendar-event fs-1 text-primary"></i>
              </div>
              <h3 className="mb-1">{summary?.total_events || events.length}</h3>
              <p className="text-muted mb-0 small">Events</p>
            </Card.Body>
          </Card>
        </div>

        <div className="summary-card-col">
          <Card
            className={`h-100 summary-card ${filters.need_review ? 'summary-card-active' : ''}`}
            onClick={() => handleNeedFilterToggle('need_review')}
            style={{ cursor: 'pointer' }}
          >
            <Card.Body className="text-center p-2">
              <div className="mb-2">
                <i className="bi bi-clipboard-check fs-1 text-warning"></i>
              </div>
              <h3 className="mb-1">
                {summary?.need_review || 0}
              </h3>
              <p className="text-muted mb-0 small">Need Review</p>
            </Card.Body>
          </Card>
        </div>

        <div className="summary-card-col">
          <Card
            className={`h-100 summary-card ${filters.need_tech_setup ? 'summary-card-active' : ''}`}
            onClick={() => handleNeedFilterToggle('need_tech_setup')}
            style={{ cursor: 'pointer' }}
          >
            <Card.Body className="text-center p-2">
              <div className="mb-2">
                <i className="bi bi-gear fs-1 text-info"></i>
              </div>
              <h3 className="mb-1">
                {summary?.need_tech_setup || 0}
              </h3>
              <p className="text-muted mb-0 small">Need Tech Setup</p>
            </Card.Body>
          </Card>
        </div>

        <div className="summary-card-col">
          <Card
            className={`h-100 summary-card ${filters.need_pre_program_email ? 'summary-card-active' : ''}`}
            onClick={() => handleNeedFilterToggle('need_pre_program_email')}
            style={{ cursor: 'pointer' }}
          >
            <Card.Body className="text-center p-2">
              <div className="mb-2">
                <i className="bi bi-envelope fs-1 text-primary"></i>
              </div>
              <h3 className="mb-1">
                {summary?.need_pre_program_email || 0}
              </h3>
              <p className="text-muted mb-0 small">Need Pre-Program Email</p>
            </Card.Body>
          </Card>
        </div>

        <div className="summary-card-col">
          <Card
            className={`h-100 summary-card ${filters.need_reminder_email ? 'summary-card-active' : ''}`}
            onClick={() => handleNeedFilterToggle('need_reminder_email')}
            style={{ cursor: 'pointer' }}
          >
            <Card.Body className="text-center p-2">
              <div className="mb-2">
                <i className="bi bi-bell fs-1 text-warning"></i>
              </div>
              <h3 className="mb-1">
                {summary?.need_reminder_email || 0}
              </h3>
              <p className="text-muted mb-0 small">Need Reminder Email</p>
            </Card.Body>
          </Card>
        </div>

        <div className="summary-card-col">
          <Card
            className={`h-100 summary-card ${filters.need_sling_shift ? 'summary-card-active' : ''}`}
            onClick={() => handleNeedFilterToggle('need_sling_shift')}
            style={{ cursor: 'pointer' }}
          >
            <Card.Body className="text-center p-2">
              <div className="mb-2">
                <i className="bi bi-calendar-x fs-1 text-danger"></i>
              </div>
              <h3 className="mb-1">
                {summary?.need_sling_shift || 0}
              </h3>
              <p className="text-muted mb-0 small">Need Sling Shift</p>
            </Card.Body>
          </Card>
        </div>

        <div className="summary-card-col">
          <Card
            className={`h-100 summary-card ${filters.sling_error ? 'summary-card-active' : ''}`}
            onClick={() => handleNeedFilterToggle('sling_error')}
            style={{ cursor: 'pointer' }}
          >
            <Card.Body className="text-center p-2">
              <div className="mb-2">
                <i className="bi bi-exclamation-triangle-fill fs-1 text-danger"></i>
              </div>
              <h3 className="mb-1">
                {summary?.sling_error || 0}
              </h3>
              <p className="text-muted mb-0 small">Sling Errors</p>
            </Card.Body>
          </Card>
        </div>
      </div>
    </>
  );

  return (
    <DashboardLayout
      title="Event Schedule"
      subtitle=""
      stickyHeader={stickyHeaderContent}
      scrollable
    >
      <style>{`
        /* Summary card minimum width */
        .summary-cards-row {
          display: flex !important;
          flex-wrap: wrap !important;
          gap: 1rem !important;
          margin-bottom: 1rem !important;
        }

        .summary-card-col {
          min-width: 175px !important;
          flex: 1 1 175px !important;
        }

        /* Clickable summary card styling */
        .summary-card {
          transition: all 0.2s ease;
          border: 2px solid transparent !important;
        }

        .summary-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
        }

        .summary-card-active {
          border: 2px solid #0d6efd !important;
          background-color: rgba(13, 110, 253, 0.05);
        }
      `}</style>

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error?.message || 'Failed to load events'}
            </Alert>
          </Col>
        </Row>
      )}

      <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: '340px', overflow: 'hidden' }}>
            <Card.Header className="bg-primary text-white flex-shrink-0">
              <div className="d-flex justify-content-between align-items-center m-1 flex-wrap gap-2">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <div className="bg-header-control rounded px-2 py-1 d-flex align-items-center gap-2">
                    <span className="text-primary fw-semibold">
                      <i className="bi bi-table me-1"></i>
                      Events for
                    </span>
                    <DatePeriodNavigation
                      onPeriodChange={handlePeriodChange}
                      initialDateRange={{ start_date: initialDateParams.start_date, end_date: initialDateParams.end_date }}
                      initialNavigationMode={initialDateParams.period}
                    />
                    <div className="border-end border-primary mx-2" style={{ height: '24px' }}></div>
                    <Button
                      variant={activeFilterCount > 0 ? "primary" : "outline-primary"}
                      size="sm"
                      onClick={() => setShowFilterBar(!showFilterBar)}
                    >
                      <i className="bi bi-funnel me-1"></i>
                      Filters
                      {activeFilterCount > 0 && (
                        <span className="badge bg-light text-primary ms-1">{activeFilterCount}</span>
                      )}
                    </Button>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3 flex-wrap">
                  <div className="bg-header-control rounded px-2 py-1">
                    <SyncButton
                      claimId="events-sync"
                      onClick={handleSyncEvents}
                      variant="outline-primary"
                      buttonText="Sync Events"
                      icon="bi-arrow-repeat"
                    />
                  </div>
                  <div className="rounded px-3 py-1">
                    <span className="text-white">
                      {events.length > 0 ? (
                        <>
                          {summary.total_events} total events
                        </>
                      ) : (
                        'No events found'
                      )}
                    </span>
                  </div>
                </div>
              </div>
              {showFilterBar && (
                <EventFilterBar
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  onClearFilters={handleClearFilters}
                />
              )}
            </Card.Header>
            <Card.Body
              ref={cardBodyRef}
              className="flex-grow-1 overflow-hidden"
              style={{
                padding: 0,
                minHeight: 0,
                transition: 'background-color 0.5s ease',
                backgroundColor: justRefreshed ? 'var(--bs-info-bg-subtle)' : 'transparent'
              }}
            >
              <DataTable
                data={events}
                columns={columns}
                getRowKey={getRowKey}
                sortConfig={{ field: sortField, direction: sortDirection }}
                onSortChange={handleSortChange}
                highlightedRows={new Set(modifiedEvents.keys())}
                modifiedCells={modifiedEvents}
                pagination={{
                  hasMore,
                  limit: FETCH_LIMIT,
                }}
                onLoadMore={handleLoadMore}
                isLoading={isLoading}
                preferencesKey="events-new"
                userId={user?.id}
                userPreferences={userData?.preferences}
                maxHeight="100%"
                enableColumnReorder={true}
                enableColumnResize={true}
                enableColumnVisibility={true}
                enableStickyHeader={true}
              />
            </Card.Body>
      </Card>

      {/* Event Edit Modal */}
      <EventEditModal
        show={showEditModal}
        onHide={handleCloseModal}
        event={editingEvent}
        field={editingField}
        onSave={handleSaveEvent}
        isLoading={updateEventMutation.isPending}
        error={editError}
      />

      {/* Host Detail Modal */}
      <HostDetailModal
        show={showHostModal}
        onHide={() => { setShowHostModal(false); setHostEventId(null); }}
        event={hostEventId ? events.find(e => e.id === hostEventId) || null : null}
        canEdit={canEditEvents}
        onEventUpdated={(updatedEvent) => {
          setAllEvents(prev => prev.map(e => e.id === updatedEvent.id ? updatedEvent : e));
        }}
      />
      {/* Invoice Detail Modal */}
      <InvoiceDetailModal
        show={!!invoiceModalId}
        onHide={() => setInvoiceModalId(null)}
        invoiceId={invoiceModalId}
      />
    </DashboardLayout>
  );
};

export default EventsNew;
